import { catchError, from, Observable, of, switchMap, tap, throwError } from 'rxjs';

import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector, runInInjectionContext } from '@angular/core';

import { AuthenticationService } from '../services/authentication/authentication.service';
import { SnackbarService } from '../services/snackbar-service/snackbar-service.service';

/**
 * Uma tela que dispara quatro requisicoes numa sessao morta mostrava quatro
 * snackbars e chamava logout() quatro vezes. Este latch deixa passar so o
 * primeiro aviso; volta a false assim que uma requisicao responde de novo.
 */
let sessionNoticeShown = false;

/** Uso exclusivo de teste: o latch e de modulo, entao vaza entre specs. */
export function resetSessionNotice(): void {
  sessionNoticeShown = false;
}

/**
 * So os hosts da ITCnet recebem o Bearer. endsWith('.itcnet.com.br') nao
 * aceita lookalike ("itcnet.com.br.evil.com" termina em "evil.com"); URL
 * relativa e o proprio host da aplicacao, entao conta como nossa.
 */
function isApiRequest(url: string): boolean {
  let hostname: string;
  try {
    hostname = new URL(url).hostname;
  } catch {
    return true;
  }
  return hostname === 'itcnet.com.br' || hostname.endsWith('.itcnet.com.br');
}

export const keycloakHttpInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  // Fora da allowlist a requisicao segue intocada: sem token (uma URL assinada
  // do GCS buscada "direito" entregaria a sessao ao storage.googleapis.com),
  // sem aviso de sessao e sem logout num 401 que nao e do Keycloak.
  if (!isApiRequest(req.url)) {
    return next(req);
  }

  const _authenticationService = inject(AuthenticationService);
  const _snackbarService = inject(SnackbarService);

  // O app sobe sem esperar o keycloak.init(), e o Angular roda os guards de uma
  // rota ao mesmo tempo: o AppGuard chama a API enquanto o AuthGuard ainda
  // espera o init, e sem sessao esta requisicao seria abortada logo abaixo.
  // Segura so enquanto o init nao terminou; depois o caminho segue sincrono.
  // Reentra pelo mesmo interceptor (inject() precisa do contexto de injecao);
  // switchMap: cancelar durante a espera nao envia nada.
  if (!_authenticationService.inicializado()) {
    const injector = inject(Injector);
    return from(_authenticationService.aguardarInit()).pipe(
      switchMap(() => runInInjectionContext(injector, () => keycloakHttpInterceptor(req, next)))
    );
  }

  const noticeOnce = (message: string, logout: boolean): void => {
    if (sessionNoticeShown) {
      return;
    }
    sessionNoticeShown = true;
    _snackbarService.openFailureSnackBar(message);
    if (logout) {
      _authenticationService.logout();
    }
  };

  if (!_authenticationService.isLoggedIn()) {
    noticeOnce('Sem token válido. Por favor, faça login novamente', false);
    return throwError(() => new Error('Not authenticated'));
  }

  const token = _authenticationService.getToken();
  if (!token) {
    noticeOnce('Sem token válido. Por favor, faça login novamente', false);
    return throwError(() => new Error('No token available'));
  }

  const send = (bearer: string): Observable<HttpEvent<unknown>> => {
    let headers = req.headers.set('Authorization', `Bearer ${bearer}`);

    // Content-Type so quando ha corpo para descrever, e sem pisar no que o
    // chamador tenha definido. FormData fica de fora de proposito: o browser
    // precisa gerar o boundary do multipart sozinho (upload de certificado).
    const hasJsonBody = req.body != null && !(req.body instanceof FormData);
    if (hasJsonBody && !headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }

    // Devolver o proprio observable do next() — e nao um new Observable em volta —
    // e o que faz o cancelamento funcionar: ao cancelar a inscricao, o backend
    // fetch aborta a requisicao de verdade.
    return next(req.clone({ headers })).pipe(
      tap(() => {
        // Respondeu: a sessao esta viva de novo, libera o proximo aviso.
        sessionNoticeShown = false;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          noticeOnce('Sessão expirada. Por favor, faça login novamente', true);
        }
        return throwError(() => error);
      })
    );
  };

  // Token com folga: segue direto, sem esperar nada. E o caminho de quase
  // toda requisicao.
  if (!_authenticationService.needsTokenRefresh()) {
    return send(token);
  }

  // O refresh automatico do keycloak-angular so roda num setTimeout marcado
  // para a hora em que o token vence. Se o timer atrasa (notebook suspenso,
  // aba em segundo plano), a requisicao sairia com o token vencido, tomaria
  // 401 e o usuario seria deslogado com a sessao ainda viva. Continua sendo
  // pipe: cancelar durante o refresh nao envia nada, e depois dele aborta o fetch.
  return from(_authenticationService.refreshTokenIfNeeded()).pipe(
    // Falha transitoria (rede, Keycloak fora do ar): o token atual ainda pode
    // valer pela folga, entao quem decide e a API.
    catchError(() => of(undefined)),
    switchMap(() => {
      // Com 400 no refresh o keycloak-js limpa o token: a sessao acabou no
      // servidor (ex.: SSO Session Max). Mesmo aviso e logout de um 401.
      const current = _authenticationService.getToken();
      if (!current) {
        noticeOnce('Sessão expirada. Por favor, faça login novamente', true);
        return throwError(() => new Error('Session expired'));
      }
      return send(current);
    })
  );
};
